import CustomTable from '@/components/molecules/CustomTable';
import CircleFillOutWithText from '@/components/molecules/WellnessStateCircle/CircleFillOutWithText';
import useScreenersByUserId from '@/hooks/assessment/useScreenersByUserId';
import { useTableColumnSort } from '@/hooks/useTableColumnSort';
import { TMemberCareMember } from '@/types/MemberCare.types';
import { TScreenerListItemApiResponse } from '@/types/Screener.types';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import { HStack, Text, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import ScreenerResultModal from '../ScreenerResultModal';

type Props = {
  memberId: string;
  member: TMemberCareMember;
};

const COLUMNS_TO_SORT_BY = ['Screener Date & Time', 'Combined Score'] as const;
const COLUMNS = [...COLUMNS_TO_SORT_BY, 'Critical Items'] as const;
export type TScreenersSortingColumn = (typeof COLUMNS_TO_SORT_BY)[number];

const ScreenersTab = ({ memberId, member }: Props) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [screenerId, setScreenerId] = React.useState<string | undefined>(undefined);

  const { sortingColumn, sortingDirection, updateSortingColumn } = useTableColumnSort<string>({
    defaultSortingColumn: 'Created At',
  });

  const {
    isLoading,
    data: screeners,
    isRefetching,
    pagination,
  } = useScreenersByUserId({
    id: memberId,
    sortingColumn,
    sortingDirection,
  });

  const data = useMemo(
    () => ({
      nodes:
        screeners?.map((screener) => ({
          ...screener,
          id: screener._id,
        })) ?? [],
    }),
    [screeners],
  );

  const onRowSelect = (screenerId: string | undefined) => {
    setScreenerId(screenerId);
    onOpen();
  };

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item: (typeof data.nodes)[number]) => {
        // in case old/deprecated data score is larger then 10, we need to multiply it by 10
        // Should not show up on production, but just in case
        const totalScoreX10 = item?.totalScore > 10 ? item?.totalScore : item?.totalScore * 10;

        const criticalItems = item?.critical?.questions.filter((q) => q.value === true) ?? [];
        const isCritical = criticalItems.length > 0;

        return {
          id: item._id,
          cells: [
            new Date(item.createdAt).toLocaleString(),
            !item?.totalScore ? (
              <Text variant={'urbanistSmallBold'} color="text.mediumGray">
                NO SCORE
              </Text>
            ) : (
              <HStack gap={'14px'} marginLeft={'5px'} onClick={() => onRowSelect(item._id)}>
                <CircleFillOutWithText
                  circleDiameter={25}
                  svgId={`screener${item._id}`}
                  thickness={4}
                  percentageFilled={totalScoreX10}
                  circleColor={getColorFromPercentage(totalScoreX10)}
                  showPercentageText={false}
                  boxShadow={'0px 3px 6px 0px #0E006224'}
                />
                <Text variant={'urbanistSemiBold'}>{totalScoreX10.toFixed()}</Text>
              </HStack>
            ),
            <HStack onClick={() => onRowSelect(item._id)}>
              <Tooltip
                label={
                  <VStack>
                    <Text variant={'urbanistMedium16'} color={'text.mediumBlue'}>
                      {criticalItems.map((question) => question.question).join(', ')}
                    </Text>
                  </VStack>
                }
                padding={'14px'}
                borderRadius={'6px'}
                color={'text.darkBlue'}
                backgroundColor={'extra.white'}
                boxShadow="0px 7px 35px 0px #00417930"
                minWidth={'500px'}
                placement="bottom"
              >
                <HStack>
                  <Text color={isCritical ? 'extra.darkRed' : 'text.mediumGray'} fontWeight={'600'}>
                    {criticalItems.length === 0 ? 'No Critical Items' : `${criticalItems.length}:`}
                  </Text>
                  <Text
                    variant={'urbanistSemiBold'}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                    maxWidth={'600px'}
                  >
                    {criticalItems.map((question) => question.question).join(', ')}
                  </Text>
                </HStack>
              </Tooltip>
            </HStack>,
          ],
        };
      }),
    [data],
  );

  return (
    <>
      <CustomTable<TScreenerListItemApiResponse[], string>
        noItemsMessage="No screeners found"
        columns={COLUMNS}
        columnsToSortBy={COLUMNS_TO_SORT_BY}
        sortingColumn={sortingColumn}
        sortingDirection={sortingDirection}
        isLoading={isLoading}
        isRefetching={isRefetching}
        updateSortingColumn={updateSortingColumn}
        pagination={pagination}
        hasRowSelect
        fullWidth
        navigateOnRowSelect={false}
        onRowSelect={(screenerId) => onRowSelect(screenerId)}
        dataWithId={data.nodes}
        tableListItems={tableListItems}
        customColumnGridPercentages={[25, 20, 55]}
        marginBottom={'0px'}
      />
      <ScreenerResultModal
        member={member}
        memberId={memberId}
        screenerId={screenerId}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
};

export default ScreenersTab;
